// Persistent, in-app-shell run bar shown while a Power Dial session is active.
//
// Hydrates from /api/powerdial/sessions/active on mount, then subscribes to
// per-session socket events for live updates. Falls back to 3-second polling
// when the socket is disconnected.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Zap, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../store/auth.js';
import { usePowerDial } from '../state/powerDial.js';
import { powerdialApi } from '../lib/powerdial.js';
import { apiGet } from '../lib/api.js';
import { getSocket } from '../lib/socket.js';
import PowerDialModal from './PowerDialModal.jsx';
import ResumeSessionBanner, { RESUMABLE_QUERY_KEY } from './powerdial/ResumeSessionBanner.jsx';
import LeadDrawer from './LeadDrawer.jsx';
import { setActiveCallGlobal } from './PhonePanel.jsx';
import { getActiveCall, placeCall as softphonePlaceCall, on as onVoiceEvent, hangUp as softphoneHangUp, resetActiveCallState } from '../lib/twilioVoice.js';
import { stop as stopProgressTone } from '../lib/progressTone.js';
import { restart as restartProgressTone, start as startProgressTone, playSessionEndedTone, playConnectedTone } from '../lib/progressTone.js';
import RunBarMemberChip from './powerdial/RunBarMemberChip.jsx';
import RunBarControls from './powerdial/RunBarControls.jsx';
import PowerDialScreenPop from './powerdial/PowerDialScreenPop.jsx';
import { popAfterEnd, POP_END_EVENTS, shouldPlayRingTone } from './powerdial/screenPopState.js';
import { formatPhone } from './powerdial/powerDialConstants.js';

export { formatPhone };

// Decide whether a connected lead should surface the active-call panel, and if
// so bind it. Pops ONLY for multi-line (conference) sessions — single-line has
// its own Twilio `incoming`→Answer-overlay path. Pure/injectable so it can be
// unit-tested without rendering (the app's vitest runs in the `node` env).
export function maybeSurfaceConnectedCall({ session, payload, buildActiveCallFromConnected, setActiveCall }) {
  if (payload?.callSid) {
    setActiveCall(buildActiveCallFromConnected({
      leadId: payload.leadId,
      callSid: payload.callSid,
      callId: payload.callId,
    }));
    return true;
  }
  return false;
}

// Decide the lead-detail route to open when a lead connects. Returns
// `/leads/<leadId>` when the payload carries a leadId AND we are not already on
// that lead's page; returns null otherwise. Pure so it can be unit-tested.
export function leadRouteForConnected(payload, currentPath) {
  if (!payload?.leadId) return null;
  const route = `/leads/${payload.leadId}`;
  return currentPath === route ? null : route;
}

/**
 * Is this Twilio Call leg still up? A leg without a status() (test doubles / older
 * SDKs) is treated as live; 'closed' is the only dead state.
 * @param {{status?: () => string}|null|undefined} call
 */
export function isLegLive(call) {
  if (!call) return false;
  if (typeof call.status !== 'function') return true;
  try { return call.status() !== 'closed'; } catch (_) { return true; }
}

// Self-heal the agent's conference leg for multi-line sessions.
// The agent leg is the room's MODERATOR (endConferenceOnExit), so a live leg must
// never be disconnected on connect: that ended the room and dropped the lead who
// had just answered (green flash at 0:00, then hang-up / next set). Only a missing
// or closed leg is re-joined.
export function maybeRejoinConference({ session, getActiveCall, placeCall }) {
  if ((session?.concurrency || 0) <= 1 || !session?._id) return false;
  if (isLegLive(getActiveCall())) return false;
  try { placeCall('conference', { pdSession: session._id }); } catch (_) {}
  return true;
}

// Force a fresh conference join on RESUME.
export function resumeRejoinConference({ session, getActiveCall, placeCall }) {
  if ((session?.concurrency || 0) <= 1 || !session?._id) return false;
  const stale = getActiveCall();
  if (stale && typeof stale.disconnect === 'function') {
    try { stale.disconnect(); } catch (_) {}
  }
  try { placeCall('conference', { pdSession: session._id }); } catch (_) {}
  return true;
}

// Terminal statuses — run bar hides when session reaches one of these.
const TERMINAL = new Set(['completed', 'cancelled']);

export default function PowerDialRunBar() {
  const { token, agent } = useAuth();
  const { session, setSession, leadIndex, mergeLeadIndex } = usePowerDial();
  const [busyAction, setBusyAction] = useState(null);
  const [screenPopLeadId, setScreenPopLeadId] = useState(null);
  // Auto-opened answered-lead panel ({ leadData, callId, callSid }).
  const [answeredPop, setAnsweredPop] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const refreshHistory = () => {
    qc.invalidateQueries({ queryKey: ['call-history'] });
    qc.invalidateQueries({ queryKey: ['phone-calls'] });
  };

  // seenConnectRef: tracks the first connect per session so we don't
  // re-place the initial conference join (which is already live).
  const seenConnectRef = useRef(null);
  const dismissTimerRef = useRef(null);
  // Guards the session-ended chime — fires only once per session._id.
  const hasPlayedEndedToneRef = useRef(null);
  // Guards the connected chime — fires only once per callSid.
  const hasPlayedConnectedToneRef = useRef(null);
  // Set while a pause/resume request is in flight so a 3s poll that raced the
  // request can't flip the status back (or blank the bar) mid-transition.
  const pendingStatusRef = useRef(null);
  const awaitingDispositionRef = useRef(false);
  // Session ids the agent explicitly ended — polls/sockets must never resurrect them.
  const endedSessionIdsRef = useRef(new Set());

  // Names for chips whose lead the modal never indexed (session started from the
  // dashboard / resumed / page reload) — fetched once per lead, tenant-scoped by the API.
  const requestedNamesRef = useRef(new Set());
  useEffect(() => {
    const missing = (session?.currentSet || [])
      .map((m) => String(m.leadId))
      .filter((id) => id && !leadIndex[id] && !requestedNamesRef.current.has(id));
    missing.forEach((id) => {
      requestedNamesRef.current.add(id);
      qc.fetchQuery({ queryKey: ['lead', id], queryFn: () => apiGet(`/leads/${id}`), staleTime: 60_000 })
        .then((r) => {
          const l = r?.lead;
          if (l) mergeLeadIndex({ [id]: { firstName: l.firstName || '', lastName: l.lastName || '', phone: l.phone || '' } });
        })
        .catch(() => requestedNamesRef.current.delete(id));
    });
  }, [session?.currentSet, leadIndex, qc, mergeLeadIndex]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      stopProgressTone();
    };
  }, []);

  // Hydrate on mount — pick up any active session from a previous page load
  useEffect(() => {
    if (!token || !agent) return;
    let cancelled = false;
    powerdialApi.getActive().then((r) => {
      if (cancelled) return;
      if (r?.session) setSession(r.session);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [token, agent, setSession]);

  // Core refresh function — merges remote session state without flashing
  const refresh = useCallback(async () => {
    const currentSid = usePowerDial.getState().session?._id;
    if (!currentSid || endedSessionIdsRef.current.has(String(currentSid))) return;
    try {
      const r = await powerdialApi.get(currentSid);
      if (!r?.session) return;
      // Ended by the agent while this request was in flight → drop the response.
      if (endedSessionIdsRef.current.has(String(currentSid))) return;

      const remote = r.session;

      // If session is still active, cancel any pending dismissal timer
      if (!TERMINAL.has(remote.status)) {
        if (dismissTimerRef.current) {
          clearTimeout(dismissTimerRef.current);
          dismissTimerRef.current = null;
        }
      }

      // Merge: only update state when something meaningful changed
      setSession((prev) => {
        if (!prev) return remote;
        // Ignore a stale poll that lands while pause/resume is in flight.
        if (pendingStatusRef.current && !TERMINAL.has(remote.status)
            && remote.status !== pendingStatusRef.current) {
          return prev;
        }
        const sameStatus = prev.status === remote.status;
        const sameSid = prev.connectedCallSid === remote.connectedCallSid;
        const sameSet = JSON.stringify(prev.currentSet) === JSON.stringify(remote.currentSet);
        const sameBlockedReason = prev.blockedReason === remote.blockedReason;
        if (sameStatus && sameSid && sameSet && sameBlockedReason) return prev;
        return { ...prev, ...remote };
      });

      // Stop progress tone when paused, or when someone is connected
      const hasConnected = Boolean(remote.connectedCallSid) ||
        (remote.currentSet || []).some((m) => m.status === 'connected');
      if (remote.status === 'paused' || hasConnected) {
        stopProgressTone();
      }

      // Handle terminal states
      if (TERMINAL.has(remote.status)) {
        stopProgressTone();

        // Nothing was ever dialed (all failed/blocked): no chime, and keep the
        // bar pinned so the agent sees what happened (they Dismiss it).
        const nothingDialed = (remote.results || []).length > 0
          && (remote.results || []).every((r) => !(r.callSids && r.callSids.length));
        if (remote.status === 'completed' && (nothingDialed || remote.blockedReason)) {
          if (dismissTimerRef.current) { clearTimeout(dismissTimerRef.current); dismissTimerRef.current = null; }
          return;
        }

        // Play ended chime once per session
        if (hasPlayedEndedToneRef.current !== remote._id) {
          hasPlayedEndedToneRef.current = remote._id;
          playSessionEndedTone();
        }

        // Tear down softphone leg
        const prev = usePowerDial.getState().session;
        if (!prev || !TERMINAL.has(prev.status)) {
          try { softphoneHangUp(); } catch (_) {}
          try { resetActiveCallState(); } catch (_) {}
          try { setActiveCallGlobal?.({ action: 'clear' }); } catch (_) {}
        }

        // Auto-dismiss run bar after 4s
        if (!dismissTimerRef.current) {
          dismissTimerRef.current = setTimeout(() => {
            const cur = usePowerDial.getState().session;
            if (cur && TERMINAL.has(cur.status)) setSession(null);
          }, 4000);
        }
      }
    } catch (_) {}
  }, [setSession]);

  // Socket subscribe + 3s poll fallback
  useEffect(() => {
    if (!session?._id) return;
    const sessionId = session._id;
    const sock = getSocket();
    sock?.emit?.('powerdial:session:subscribe', sessionId);

    const onUpdate = () => refresh();

    const onConnected = (payload) => {
      // Stop ringing immediately — callee answered
      stopProgressTone();

      // Play connected chime once per callSid
      if (payload?.callSid && hasPlayedConnectedToneRef.current !== payload.callSid) {
        hasPlayedConnectedToneRef.current = payload.callSid;
        playConnectedTone();
      }

      refresh();

      // Screen-pop is handled by `dialer:lead-answered` (zero extra fetch).

      // Auto-navigate to lead page (guard against duplicate nav)
      const route = leadRouteForConnected(payload, location.pathname);
      if (route) navigate(route);

      // Self-heal: re-join conference for multi-line if agent leg dropped
      const store = usePowerDial.getState();
      const sid = store.session?._id || null;
      const isFirstConnect = seenConnectRef.current !== sid;
      seenConnectRef.current = sid;
      maybeRejoinConference({
        session: store.session,
        getActiveCall,
        placeCall: softphonePlaceCall,
      });

      maybeSurfaceConnectedCall({
        session: store.session,
        payload,
        buildActiveCallFromConnected: store.buildActiveCallFromConnected,
        setActiveCall: setActiveCallGlobal,
      });
    };

    const onSessionPaused = (payload) => {
      stopProgressTone();
      if (payload?.reason || payload?.blockedReason) {
        setSession((prev) => (prev ? {
          ...prev,
          status: 'paused',
          blockedReason: payload.blockedReason || (payload.reason === 'blocked' ? prev.blockedReason : payload.reason),
        } : prev));
      }
      refresh();
    };

    const onSessionCompleted = (payload) => {
      stopProgressTone();
      if (payload?.blockedReason) {
        if (dismissTimerRef.current) {
          clearTimeout(dismissTimerRef.current);
          dismissTimerRef.current = null;
        }
        setSession((prev) => (prev ? {
          ...prev,
          status: 'completed',
          blockedReason: payload.blockedReason,
        } : prev));
      }
      refresh();
    };

    // First leg answered: open the lead panel instantly, no clicks.
    const onLeadAnswered = (payload) => {
      if (!payload?.leadId) return;
      setAnsweredPop(payload);
    };

    // Server parks the session after a connected call so the agent can log a
    // disposition. This is not an error — auto-resume once they're done.
    // Legacy servers parked the session after a connected call until a
    // disposition was logged. Dispositions are optional now: keep dialing.
    const onAwaitingDisposition = () => {
      awaitingDispositionRef.current = false;
      refresh();
      if (usePowerDial.getState().session?.status === 'paused') handleResume().catch(() => {});
    };

    const onSessionResumed = () => {
      refresh();
    };

    const onAmdCorrection = (payload) => {
      setAnsweredPop((p) => popAfterEnd(p, payload, { voicemail: true }));
      refresh();
      stopProgressTone();
      if (payload?.leadId && String(payload.leadId) === String(screenPopLeadId)) {
        setScreenPopLeadId(null);
      }
      toast('Voicemail detected — advancing to next lead.', { icon: '📬' });
      const currentLeadId = location.pathname.startsWith('/leads/')
        ? location.pathname.split('/')[2]
        : null;
      if (payload?.leadId && currentLeadId && String(payload.leadId) === String(currentLeadId)) {
        navigate('/leads');
      }
    };

    const onCallConnected = (payload) => {
      if (payload?.leadId) setScreenPopLeadId(payload.leadId);
    };

    // Surface Twilio Voice SDK errors during a Power Dial session as visible toasts
    const offVoiceError = onVoiceEvent('error', (data) => {
      const msg = data?.message || 'Phone error';
      if (usePowerDial.getState().session) {
        toast.error(`Phone: ${msg}`);
      }
    });

    sock?.on?.('powerdial:session:started', onUpdate);
    sock?.on?.('powerdial:session:paused', onSessionPaused);
    sock?.on?.('powerdial:session:resumed', onSessionResumed);
    sock?.on?.('powerdial:session:completed', onSessionCompleted);
    sock?.on?.('powerdial:session:cancelled', onUpdate);
    sock?.on?.('powerdial:set:started', onUpdate);
    sock?.on?.('powerdial:set:completed', onUpdate);
    sock?.on?.('powerdial:set:skipped', onUpdate);
    sock?.on?.('powerdial:awaiting_disposition', onAwaitingDisposition);
    sock?.on?.('powerdial:lead:status', onUpdate);
    sock?.on?.('powerdial:lead:connected', onConnected);
    sock?.on?.('dialer:lead-answered', onLeadAnswered);
    sock?.on?.('call:connected', onCallConnected);
    sock?.on?.('call:answered', onCallConnected);
    sock?.on?.('powerdial:amd_correction', onAmdCorrection);
    // The answered call is over: close the left screen-pop (kept open, marked
    // ended, only if the agent already started a disposition or notes).
    const onPopCallEnded = (payload) => setAnsweredPop((p) => popAfterEnd(p, payload));
    POP_END_EVENTS.forEach((ev) => sock?.on?.(ev, onPopCallEnded));

    const poll = setInterval(refresh, 3000);

    return () => {
      // NOTE: do not stop the ring tone here — this effect re-runs on every route /
      // pop change and used to silence the ringing mid-dial. The tone is driven by
      // the `shouldRing` effect below.
      sock?.emit?.('powerdial:session:unsubscribe', sessionId);
      sock?.off?.('powerdial:session:started', onUpdate);
      sock?.off?.('powerdial:session:paused', onSessionPaused);
      sock?.off?.('powerdial:session:resumed', onSessionResumed);
      sock?.off?.('powerdial:session:completed', onSessionCompleted);
      sock?.off?.('powerdial:session:cancelled', onUpdate);
      sock?.off?.('powerdial:set:started', onUpdate);
      sock?.off?.('powerdial:set:completed', onUpdate);
      sock?.off?.('powerdial:set:skipped', onUpdate);
      sock?.off?.('powerdial:awaiting_disposition', onAwaitingDisposition);
      sock?.off?.('powerdial:lead:status', onUpdate);
      sock?.off?.('powerdial:lead:connected', onConnected);
      sock?.off?.('dialer:lead-answered', onLeadAnswered);
      sock?.off?.('call:connected', onCallConnected);
      sock?.off?.('call:answered', onCallConnected);
      sock?.off?.('powerdial:amd_correction', onAmdCorrection);
      POP_END_EVENTS.forEach((ev) => sock?.off?.(ev, onPopCallEnded));
      if (typeof offVoiceError === 'function') offVoiceError();
      clearInterval(poll);
    };
  }, [session?._id, refresh, navigate, location.pathname, screenPopLeadId]);

  // Ring tone follows the session: audible whenever a running session is dialing a
  // set and nobody is connected — for EVERY set (next set / skip / voicemail redial),
  // not just the first one — and silent otherwise.
  const shouldRing = shouldPlayRingTone(session);
  useEffect(() => {
    if (shouldRing) {
      try { startProgressTone(); } catch (_) {}
    } else {
      stopProgressTone();
    }
  }, [shouldRing]);

  // ------------------------------------------------------------------
  // Action helpers
  // ------------------------------------------------------------------
  const wrap = async (key, fn, msg) => {
    setBusyAction(key);
    try {
      const r = await fn();
      // Merge — never replace the session with an empty/partial response, so
      // the run bar stays pinned through pause/resume.
      if (r?.session && r.session._id) {
        setSession((prev) => (prev ? { ...prev, ...r.session } : r.session));
      }
      if (msg) toast.success(msg);
      return r;
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Action failed');
      throw err;
    } finally {
      setBusyAction(null);
    }
  };

  const getSessionId = () => session?._id || usePowerDial.getState().session?._id;

  const handlePause = () => wrap('pause', async () => {
    stopProgressTone();
    const sid = getSessionId();
    if (!sid) throw new Error('No active session to pause');
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    // Optimistic update so run bar stays pinned
    pendingStatusRef.current = 'paused';
    setSession((prev) => (prev ? { ...prev, status: 'paused' } : prev));
    try {
      return await powerdialApi.pause(sid);
    } catch (err) {
      // Roll back so the Pause control is usable again.
      setSession((prev) => (prev ? { ...prev, status: 'running' } : prev));
      throw err;
    } finally {
      pendingStatusRef.current = null;
    }
  }, 'Paused');

  // Resume: restart ringing tone, then tell backend, then re-join conference.
  const handleResume = () => wrap('resume', async () => {
    const sid = getSessionId();
    if (!sid) throw new Error('No active session to resume');

    // Start ringing immediately so agent hears feedback right away
    try { restartProgressTone(); } catch (_) {}

    // Optimistic: flip status to 'running' so the run bar updates instantly
    pendingStatusRef.current = 'running';
    setSession((prev) => (prev ? { ...prev, status: 'running' } : prev));

    let r;
    try {
      r = await powerdialApi.resume(sid);
    } catch (err) {
      stopProgressTone();
      setSession((prev) => (prev ? { ...prev, status: 'paused' } : prev));
      throw err;
    } finally {
      pendingStatusRef.current = null;
    }
    if (r?.session) {
      setSession((prev) => (prev ? { ...prev, ...r.session } : r.session));
    }

    // Re-join conference for multi-line
    resumeRejoinConference({
      session: r?.session || usePowerDial.getState().session,
      getActiveCall,
      placeCall: softphonePlaceCall,
    });

    return r;
  }, 'Resumed');

  // Disposition saved/closed in the Phone Panel → continue the queue.
  useEffect(() => {
    const onDone = () => {
      if (!awaitingDispositionRef.current) return;
      awaitingDispositionRef.current = false;
      const cur = usePowerDial.getState().session;
      if (cur?.status === 'paused') handleResume().catch(() => {});
    };
    window.addEventListener('civix:disposition:done', onDone);
    return () => window.removeEventListener('civix:disposition:done', onDone);
  });

  // Agent-initiated hangup on the currently-connected leg
  const handleHangupConnected = (callSid) => wrap('hangup-connected', async () => {
    const sid = getSessionId();
    if (!sid) return;
    // Multi-line: the agent's softphone leg IS the conference room — drop only
    // the lead leg server-side so the next line can bridge in (Wavv-style).
    if ((session?.concurrency || 0) <= 1) {
      try { softphoneHangUp(); } catch (_) {}
      try { setActiveCallGlobal?.({ action: 'clear' }); } catch (_) {}
    }
    const leg = typeof callSid === 'string' ? callSid : undefined; // ignore click events
    return powerdialApi.hangupConnected(sid, leg);
  }, 'Hung up — advancing');

  const handleEndSession = () => wrap('cancel', async () => {
    stopProgressTone();
    const sid = getSessionId();
    // No chime when merely dismissing an already-ended session.
    const alreadyEnded = TERMINAL.has(usePowerDial.getState().session?.status);
    if (sid && !alreadyEnded && hasPlayedEndedToneRef.current !== sid) {
      hasPlayedEndedToneRef.current = sid;
      playSessionEndedTone();
    }
    try { softphoneHangUp(); } catch (_) {}
    try { resetActiveCallState(); } catch (_) {}
    try { setActiveCallGlobal?.({ action: 'clear' }); } catch (_) {}
    if (!sid) {
      setSession(null);
      return { session: null };
    }
    // Block background polls / socket refreshes from re-hydrating this session
    // while (and after) the cancel is in flight.
    endedSessionIdsRef.current.add(String(sid));
    try {
      await powerdialApi.cancel(sid);
    } catch (err) {
      endedSessionIdsRef.current.delete(String(sid));
      throw err;
    }
    // Session summary + every attempt are written server-side on cancel.
    refreshHistory();
    // Offer "Resume where I left off" if leads remain.
    qc.invalidateQueries({ queryKey: RESUMABLE_QUERY_KEY });
    if (dismissTimerRef.current) { clearTimeout(dismissTimerRef.current); dismissTimerRef.current = null; }
    awaitingDispositionRef.current = false;
    try { getSocket()?.emit?.('powerdial:session:unsubscribe', sid); } catch (_) {}
    // Unmount the bar immediately; returning no session skips wrap()'s merge.
    setSession(null);
    usePowerDial.getState().closeModal?.();
    return { session: null };
  }, 'Session ended — saved to Call History');

  const hasConnectedLeg = Boolean(session?.connectedCallSid);

  const handleToggleSmartLocalId = () => wrap('toggle-smart-local', async () => {
    const sid = getSessionId();
    if (!sid) return;
    const nextVal = session.smartLocalId === false;
    return powerdialApi.update(sid, { smartLocalId: nextVal });
  }, session?.smartLocalId === false ? 'Smart Local ID enabled' : 'Smart Local ID disabled');

  const handleSkipSet = () => wrap('skip', async () => {
    const sid = getSessionId();
    if (!sid) throw new Error('No active session to skip');
    const r = await powerdialApi.skip(sid);
    refreshHistory();
    return r;
  }, 'Skipped — dialing next').catch((err) => {
    // 409 = the server already ended/finished this session; resync the bar
    // instead of leaving it stale (wrap() already toasted the reason).
    if (err?.response?.status === 409) refresh();
  });

  // Determine if the run bar should be visible
  // Stay mounted for every session state (incl. ended) — it auto-dismisses via
  // the 4s timer / setSession(null), never via a status-based unmount.
  const showRunBar = Boolean(session);

  // Status display label
  const statusLabel = session?.status === 'queued' ? 'Starting…'
    : session?.status === 'running' ? 'Dialing'
    : session?.status === 'paused' ? 'Paused'
    : TERMINAL.has(session?.status) ? 'Ended'
    : session?.status || '';

  // Live indicator color
  const statusColor = session?.status === 'running' ? 'text-emerald-400'
    : session?.status === 'paused' ? 'text-amber-400'
    : 'text-civix-purple';

  return (
    <>
      <PowerDialModal />
      {!showRunBar && <ResumeSessionBanner />}
      {showRunBar ? (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-4 z-900 w-full max-w-5xl xl:max-w-6xl px-4"
          role="region"
          aria-label="Power Dial run bar"
        >
          <div className="rounded-2xl bg-[#0B0C14]/95 backdrop-blur-xl border border-civix-purple/40 ring-1 ring-white/10 shadow-[0_12px_40px_rgba(0,0,0,0.7)] p-3.5 transition-all">
            <div className="flex items-center justify-between gap-4">
              {/* Left: identity + stats */}
              <div className="flex items-center gap-2.5 text-white shrink-0">
                <div className="relative w-8 h-8 rounded-xl bg-linear-to-br from-[#7C3AED] to-[#E040FB] grid place-items-center shadow-md shadow-civix-purple/30">
                  <Zap className="w-4 h-4 text-white" />
                  {session.status === 'running' && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0B0C14] animate-pulse" />
                  )}
                </div>
                <div className="text-xs">
                  <div className="font-semibold flex items-center gap-1.5">
                    <span>Power Dial</span>
                    <span className="text-white/40">·</span>
                    <span className={`capitalize font-medium ${statusColor}`}>{statusLabel}</span>
                    {session.status === 'paused' && (
                      <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                        Session Paused
                      </span>
                    )}
                  </div>
                  <div className="text-white/50 text-[11px]">
                    {session.results?.length || 0} of {session.selectedLeadIds?.length || 0} done
                    {' · '}{session.concurrency} lines · {session.redialAttempts} redial
                  </div>
                </div>
              </div>

              {/* Center: current set member chips */}
              <div className="flex-1 min-w-0">
                <div
                  className="grid gap-2 py-0.5"
                  style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}
                >
                  {(session.currentSet || []).map((m) => {
                    const idx = leadIndex[String(m.leadId)] || {};
                    return (
                      <RunBarMemberChip
                        key={String(m.leadId)}
                        member={m}
                        session={session}
                        leadInfo={idx}
                        onSelect={() => setScreenPopLeadId(m.leadId)}
                      />
                    );
                  })}
                  {(!session.currentSet || session.currentSet.length === 0) && (
                    <div className="text-xs text-white/40 italic px-3 py-2 flex items-center gap-1.5">
                      {session.status === 'paused' || session.blockedReason ? (
                        <><span>⏸</span> {session.blockedReason || 'Session Paused — click Resume to continue'}</>
                      ) : (
                        <>
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-civix-purple animate-pulse" />
                          Preparing next set…
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: action controls (onClick={handleResume}) */}
              <RunBarControls
                session={session}
                busyAction={busyAction}
                hasConnectedLeg={hasConnectedLeg}
                onToggleSmartLocalId={handleToggleSmartLocalId}
                onHangupConnected={handleHangupConnected}
                onPause={handlePause}
                onResume={handleResume}
                onSkipSet={handleSkipSet}
                onEndSession={handleEndSession}
              />
            </div>
            {Boolean(session.blockedReason) && (
              <div role="alert" className="mt-2.5 px-3 py-2 rounded-lg text-xs bg-amber-500/10 border border-amber-500/30 text-amber-200">
                ⏸ {session.blockedReason}
                {session.status === 'completed' && ' Click Dismiss to close.'}
              </div>
            )}
          </div>
        </div>
      ) : null}
      {answeredPop && (
        <PowerDialScreenPop
          pop={answeredPop}
          onClose={() => setAnsweredPop(null)}
          onEndCall={async (callSid) => {
            await handleHangupConnected(callSid);
            setAnsweredPop((p) => (p ? { ...p, ended: true } : p));
          }}
          onDirty={() => setAnsweredPop((p) => (p && !p.dirty ? { ...p, dirty: true } : p))}
        />
      )}
      {screenPopLeadId && (
        <LeadDrawer
          leadId={screenPopLeadId}
          onClose={() => setScreenPopLeadId(null)}
        />
      )}
    </>
  );
}
